import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import axios from 'axios';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import {
  Box,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import {
  ChevronLeft,
  ChevronRight,
} from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';

import IntegrationFilters from './dashboard/IntegrationFilters';
import IntegrationTable from './dashboard/IntegrationTable';

// Import Google Analytics tracking utility
import { trackEvent } from '../../../../tracking/index';
import { appUrl, digitizationApiKey } from '../../../../utils/config';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';

// Material-UI styles for the integration dashboard components
const styles = (theme) => ({
  root: {
    padding: '1rem',
    backgroundColor: '#ffffff',
  },
  // Styling for quick summary cards section
  summaryContainer: {
    marginBottom: '1rem',
  },
  summaryCardGrid: {
    display: "flex",
    // justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    gap: "1rem",
    flexGrow: 1,
    flexBasis: "260px",
  },
  summaryCard: {
    cursor: 'pointer',
    backgroundColor: '#FFF8F8',
    border: '1px solid black',
    height: '115px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#FFD7D7',
    },
  },
  summaryCardNonClickable: {
    backgroundColor: '#ffffff',
    border: '1px solid black',
    height: '115px',
    width: '100%',
  },
  summaryCardSelected: {
    cursor: 'pointer',
    backgroundColor: '#FFD7D7',
    border: '1px solid black',
    height: '115px',
    width: '100%',
  },
  cardContent: {
    height: 'inherit',
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  cardInnerContainer: {
    display: 'flex',
    flexDirection: 'column !important',
    flexWrap: 'nowrap !important',
    alignItems: 'center',
    justifyContent: 'center',
    // minWidth: "11rem",
    // maxWidth: "12rem",
    // flexShrink: 1,
    flexGrow: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    padding: "2px",
  },
  cardTextContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  numberLarge: {
    textAlign: 'center',
    fontWeight: 500,
    fontSize: '26px !important',
  },
  label: {
    textAlign: 'center',
    fontSize: '16px !important',
    fontWeight: 500,
  },
  // Accordion component styling for expandable sections
  accordion: {
    boxShadow: 'none',
    border: '0.1px solid #C6C6C6',
    backgroundColor: '#FAFAFA',
    marginBottom: '1rem',
  },
  accordionSummary: {
    "&.Mui-expanded": {
      minHeight: "50px",
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: "10px 0px",
    },
  },
  // Container for filters section layout
  filtersContainer: {
    marginTop: '1rem',
    marginBottom: '1rem',
  },
});

const Integration = ({ classes, history }) => {
  // Main data and loading states
  const [integrationData, setIntegrationData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination state variables
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filter state for search and criteria
  const [filters, setFilters] = useState({
    criteria: 'all',
    searchKeyword: '',
    integrationName: '',
    jobName: '',
    vendorPlant: '',
    vendorNumber: '',
  });
  
  // Search functionality states
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState('');
  const searchTimeoutRef = useRef(null);
  
  // Summary dashboard statistics
  const [summaryData, setSummaryData] = useState({
    successful: 0,
    inProgress: 0,
    failures: 0,
    pendingRetries: 0,
    successRate: 0,
  });
  
  // UI state for last sync time and accordion
  const [lastSyncTime, setLastSyncTime] = useState(new Date());
  const [expanded, setExpanded] = useState(false);
  const [selectedSummaryCard, setSelectedSummaryCard] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [selectedSummaryType, setSelectedSummaryType] = useState(null);

  // Load data when component first mounts and when dependencies change
  useEffect(() => {
    setSummaryLoading(true);
    fetchIntegrationData();
    trackEvent('Integration Management', 'Page Load', 'dashboard');
  }, [page, rowsPerPage, debouncedSearchKeyword]);

  // Clean up search timeout when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);



  // Fetch integration data from the cron dashboard API
  const fetchIntegrationData = async (specificPage = null) => {
    setLoading(true);
    
    try {
      // Use specific page if provided, otherwise use current page state
      const currentPage = specificPage !== null ? specificPage : page;
      
      // Build API request payload with current filters
      const payload = {
        page: currentPage,
        limit: rowsPerPage
      };

      // Handle different filter criteria types
      if (filters.criteria && filters.criteria !== 'all') {
        if (filters.criteria === 'status') {
          // When status criteria is selected, use searchKeyword as status value
          if (filters.searchKeyword) {
            payload.status = filters.searchKeyword; // 'running', 'success', 'failed', 'retrying'
          }
        } else if (filters.criteria === 'integration_name') {
          // When integration name criteria is selected
          if (filters.searchKeyword) {
            payload.integration_name = filters.searchKeyword;
          }
        } else if (filters.criteria === 'job_name') {
          // When job name criteria is selected
          if (filters.searchKeyword) {
            payload.job_name = filters.searchKeyword;
          }
        } else if (filters.criteria === 'vendor_plant') {
          // When vendor plant criteria is selected
          if (filters.searchKeyword) {
            payload.vendor_plant = parseInt(filters.searchKeyword);
          }
        } else if (filters.criteria === 'vendor_number') {
          // When vendor number criteria is selected
          if (filters.searchKeyword) {
            payload.vendor_number = parseInt(filters.searchKeyword);
          }
        }
      } else {
        // Default search behavior when no specific criteria is selected
        if (debouncedSearchKeyword) {
          payload.integration_name = debouncedSearchKeyword;
        } else if (filters.searchKeyword) {
          payload.integration_name = filters.searchKeyword;
        }
      }

      // Add individual filter fields (these are separate from criteria-based search)
      if (filters.integrationName) {
        payload.integration_name = filters.integrationName;
      }

      if (filters.jobName) {
        payload.job_name = filters.jobName;
      }

      if (filters.vendorPlant) {
        payload.vendor_plant = parseInt(filters.vendorPlant);
      }

      if (filters.vendorNumber) {
        payload.vendor_number = parseInt(filters.vendorNumber);
      }

      // Add quick summary filter if one is selected
      if (selectedSummaryType) {
        payload.quickSummary = selectedSummaryType;
      }

      const response = await axios.post(`${appUrl}/api/cron/dashboard`, payload);
    
      
      // Update pagination information from response
      if (response.data && response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalItems(response.data.pagination.totalItems || 0);
      }
      
      // Transform API data to match table structure
      const transformedData = response.data?.integrations?.map((item, index) => {
        return {
          id: (currentPage - 1) * rowsPerPage + index + 1,
          integrationId: item.id,
          integrationName: item.integration_name || 'N/A',
          jobName: item.job_name || 'N/A',
          vendorPlant: item.vendor_plant || 'N/A',
          vendorNumber: item.vendor_number || 'N/A',
          sourceTarget: `${item.source_system || 'N/A'} → ${item.target_system || 'N/A'}`,
          sourceSystem: item.source_system || 'N/A',
          targetSystem: item.target_system || 'N/A',
          type: item.type || 'N/A',
          status: item.status === 'success' ? 'Success' : 
                  item.status === 'failed' ? 'Failed' : 
                  item.status === 'running' ? 'Running' : 
                  item.status === 'retrying' ? 'Retrying' : 
                  item.status || 'N/A',
          lastRun: item.last_run ? new Date(item.last_run).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'N/A',
          duration: item.duration_seconds ? `${item.duration_seconds}s` : 'N/A',
          retryCount: item.retry_count || 0,
          errors: item.errors || 0
        };
      }) || [];
      
      setIntegrationData(transformedData);
      
      // Update summary data from the same API response
      if (response.data && response.data.summary) {
        setSummaryData({
          successful: response.data.summary.successful || 0,
          inProgress: response.data.summary.inProgress || 0,
          failures: response.data.summary.failures || 0,
          pendingRetries: response.data.summary.pendingRetries || 0,
          successRate: parseFloat(response.data.summary.successRate) || 0,
        });
      }
      setSummaryLoading(false);
      setLoading(false);
    } catch (error) {
      console.error('Integration API Error:', error);
      // Set empty array on error
      setIntegrationData([]);
      setSummaryLoading(false);
      setLoading(false);
    }
  };



  // Updates filter state when user changes filter criteria or dates
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Don't trigger API call here - only when search button is clicked
    trackEvent('Integration Management', 'Filter Change', newFilters.criteria);
  };

  // Triggers API search when user clicks the search button
  const handleSearch = () => {
    setDebouncedSearchKeyword(filters.searchKeyword);
    setPage(1);
    setLoading(true);
    setSummaryLoading(true);
    // Don't clear summary selection when searching
    trackEvent('Integration Management', 'Search', filters.searchKeyword);
  };

  // Clears all filters and resets to default state
  const handleClearFilters = () => {
    setFilters({
      criteria: 'all',
      searchKeyword: '',
      integrationName: '',
      jobName: '',
      vendorPlant: '',
      vendorNumber: '',
    });
    setDebouncedSearchKeyword('');
    setPage(1);
    setLoading(true);
    setSummaryLoading(true);
    setSelectedSummaryCard(null);
    setSelectedSummaryType(null);
    trackEvent('Integration Management', 'Clear Filters', 'all');
  };

  // Clears summary card selection
  const handleClearSummary = async () => {
    setSelectedSummaryCard(null);
    setSelectedSummaryType(null);
    setPage(1);
    
    // Call API with initial load payload (only page and limit)
    setLoading(true);
    setSummaryLoading(true);
    try {
      const payload = {
        page: 1,
        limit: rowsPerPage
      };

      const response = await axios.post(`${appUrl}/api/cron/dashboard`, payload);
 
      
      // Update pagination information from response
      if (response.data && response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalItems(response.data.pagination.totalItems || 0);
      }
      
      // Transform API data to match table structure
      const transformedData = response.data?.integrations?.map((item, index) => {
        return {
          id: (1 - 1) * rowsPerPage + index + 1,
          integrationId: item.id,
          integrationName: item.integration_name || 'N/A',
          jobName: item.job_name || 'N/A',
          vendorPlant: item.vendor_plant || 'N/A',
          vendorNumber: item.vendor_number || 'N/A',
          sourceTarget: `${item.source_system || 'N/A'} → ${item.target_system || 'N/A'}`,
          sourceSystem: item.source_system || 'N/A',
          targetSystem: item.target_system || 'N/A',
          type: item.type || 'N/A',
          status: item.status === 'success' ? 'Success' : 
                  item.status === 'failed' ? 'Failed' : 
                  item.status === 'running' ? 'Running' : 
                  item.status === 'retrying' ? 'Retrying' : 
                  item.status || 'N/A',
          lastRun: item.last_run ? new Date(item.last_run).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'N/A',
          duration: item.duration_seconds ? `${item.duration_seconds}s` : 'N/A',
          retryCount: item.retry_count || 0,
          errors: item.errors || 0
        };
      }) || [];
      
      setIntegrationData(transformedData);
      
      // Update summary data from the same API response
      if (response.data && response.data.summary) {
        setSummaryData({
          successful: response.data.summary.successful || 0,
          inProgress: response.data.summary.inProgress || 0,
          failures: response.data.summary.failures || 0,
          pendingRetries: response.data.summary.pendingRetries || 0,
          successRate: parseFloat(response.data.summary.successRate) || 0,
        });
      }
      setSummaryLoading(false);
      setLoading(false);
    } catch (error) {
      console.error('Clear Summary API Error:', error);
      setIntegrationData([]);
      setSummaryLoading(false);
      setLoading(false);
    }
    
    trackEvent('Integration Management', 'Clear Summary', 'manual');
  };

  // Updates pagination when user changes rows per page
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Manually refreshes integration data when user clicks refresh
  const handleRefresh = async (resetToPage = 1) => {
    const now = new Date();
    setLastSyncTime(now);
    
    // Reset to specific page if provided
      setPage(1);
    
    // Set loading states and reload data
    setLoading(true);
    setSummaryLoading(true);
    await fetchIntegrationData(1);
    trackEvent('Integration Management', 'Refresh', 'manual');
  };

  // Handles clicks on summary cards to filter data by specific categories
  const handleQuickSummaryClick = async (summaryType) => {
    setSelectedSummaryCard(summaryType);
    setPage(1);
    
    // Clear all filters when selecting a summary card
    setFilters({
      criteria: 'all',
      searchKeyword: '',
      integrationName: '',
      jobName: '',
      vendorPlant: '',
      vendorNumber: '',
    });
    
    // Clear debounced search keyword
    setDebouncedSearchKeyword('');
    
    // Set the summary type for future pagination calls
    let quickSummaryValue = null;
    if (summaryType === 'successful') {
      quickSummaryValue = 'successful';
    } else if (summaryType === 'inProgress') {
      quickSummaryValue = 'inProgress';
    } else if (summaryType === 'failures') {
      quickSummaryValue = 'failures';
    } else if (summaryType === 'pendingRetries') {
      quickSummaryValue = 'retrying';
    }
    setSelectedSummaryType(quickSummaryValue);
    
    setLoading(true);
    try {
      const payload = {
        page: 1,
        limit: rowsPerPage
      };

      if (summaryType === 'successful') {
        payload.quickSummary = 'successful';
      } else if (summaryType === 'inProgress') {
        payload.quickSummary = 'inProgress';
      } else if (summaryType === 'failures') {
        payload.quickSummary = 'failures';
      } else if (summaryType === 'pendingRetries') {
        payload.quickSummary = 'retrying';
      }

      const response = await axios.post(`${appUrl}/api/cron/dashboard`, payload);

      
      if (response.data && response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalItems(response.data.pagination.totalItems || 0);
      }
      
      // Transform API data to match table structure
      const transformedData = response.data?.integrations?.map((item, index) => {
        return {
          id: (page - 1) * rowsPerPage + index + 1,
          integrationId: item.id,
          integrationName: item.integration_name || 'N/A',
          jobName: item.job_name || 'N/A',
          vendorPlant: item.vendor_plant || 'N/A',
          vendorNumber: item.vendor_number || 'N/A',
          sourceTarget: `${item.source_system || 'N/A'} → ${item.target_system || 'N/A'}`,
          sourceSystem: item.source_system || 'N/A',
          targetSystem: item.target_system || 'N/A',
          type: item.type || 'N/A',
          status: item.status === 'success' ? 'Success' : 
                  item.status === 'failed' ? 'Failed' : 
                  item.status === 'running' ? 'Running' : 
                  item.status === 'retrying' ? 'Retrying' : 
                  item.status || 'N/A',
          lastRun: item.last_run ? new Date(item.last_run).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'N/A',
          duration: item.duration_seconds ? `${item.duration_seconds}s` : 'N/A',
          retryCount: item.retry_count || 0,
          errors: item.errors || 0
        };
      }) || [];
      
      setIntegrationData(transformedData);
      setLoading(false);
      
      trackEvent('Integration Management', 'Quick Summary Click', summaryType);
    } catch (error) {
      console.error('Integration Quick Summary API Error:', error);
      setIntegrationData([]);
      setLoading(false);
    }
  };

  // Use transformed data directly since API handles filtering and pagination
  const paginatedData = integrationData;

  // Show pagination controls only when there are more items than current page size
  const showPagination = totalItems > rowsPerPage;

  // Renders the summary cards with integration statistics
  const renderQuickSummaryCards = () => {
    // Define summary card configuration
    const summaryItems = [
      { count: summaryData.successful, label: 'Successful', key: 'successful', clickable: true },
      { count: summaryData.inProgress, label: 'In Progress', key: 'inProgress', clickable: true },
      { count: summaryData.failures, label: 'Failures', key: 'failures', clickable: true },
      { count: summaryData.pendingRetries, label: 'Pending Retries', key: 'pendingRetries', clickable: true },
      { count: `${summaryData.successRate}%`, label: 'Success Rate', key: 'successRate', color: '#4CA30D', clickable: false },
    ];

    return (
      <Grid container className={classes.summaryCardGrid}  style={{flexWrap: "nowrap"}}>
        {summaryItems.map((item, index) => (
          <Grid item className={classes.summaryCardGrid} key={item.key}>
            <Card 
              className={
                item.clickable 
                  ? (selectedSummaryCard === item.key ? classes.summaryCardSelected : classes.summaryCard)
                  : classes.summaryCardNonClickable
              }
              onClick={item.clickable ? () => handleQuickSummaryClick(item.key) : undefined}
            >
              <CardContent className={classes.cardContent}>
                {summaryLoading ? (
                  <Grid container className={classes.cardInnerContainer}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                      <CircularProgress size={24} style={{ marginBottom: '8px' }} />
                      <Typography className={classes.label} style={{ fontSize: '0.7rem' }}>
                        Loading...
                      </Typography>
                    </div>
                  </Grid>
                ) : (
                  <Grid container className={`quick-summary-card-wrap ${classes.cardInnerContainer}`}>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      style={{ alignItems: 'flex-end' }}
                      className={classes.cardTextContainer}
                    >
                      <Typography 
                        className={`quick-summary-count ${classes.numberLarge}`}
                        style={{ color: item.color }}
                      >
                        {typeof item.count === 'number' ? 
                          item.count.toString().padStart(2, '0') : 
                          item.count
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} className={classes.cardTextContainer}>
                      <Typography className={`quick-summary-label ${classes.label}`}>
                        {item.label}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <Grid container style={{ marginBottom: '1rem' }}>
        <Grid item xs={12}>
          <Typography style={{ fontWeight: 500, fontSize: '2rem', marginTop: '0.5rem' }}>
            Integration Management Dashboard
          </Typography>
        </Grid>
      </Grid>

      {/* Collapsible Quick Summary */}
      <Grid container className={classes.summaryContainer}>
        <Grid item xs={12}>
          <Accordion className={classes.accordion} expanded={expanded}>
            <AccordionSummary
              className={classes.accordionSummary}
              // expandIcon={<ExpandMoreIcon />}
              onClick={() => setExpanded(!expanded)}
            >
              <Grid container style={{ justifyContent: "space-between" }}>
                <Grid item>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      style={{ marginRight: "8px" }}
                      size="small"
                    >
                      {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography
                      variant="body2"
                      style={{ paddingBottom: "inherit", fontWeight: 600 }}
                    >
                      Quick Summary:
                    </Typography>
                  </div>
                </Grid>
                <Grid item>
                  {(selectedSummaryCard || selectedSummaryType) && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSummary();
                      }}
                      style={{ marginRight: "8px", color: "#EB5E28", fontSize: "12px", backgroundColor: "transparent" }}
                      title="Clear Summary Filter"
                    >
                      <ClearIcon fontSize="small" /> Clear Quick Summary
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              {renderQuickSummaryCards()}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Filters with Last Synced and PER PAGE */}
      <Grid container className={classes.filtersContainer}>
        <IntegrationFilters 
          onFilterChange={handleFilterChange} 
          filters={filters}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          lastSyncTime={lastSyncTime}
          onRefresh={handleRefresh}
          onSearch={handleSearch}
          onClearFilters={handleClearFilters}
        />
      </Grid>

      {/* Integration Table */}
      <Grid container>
        <IntegrationTable 
          data={paginatedData} 
          loading={loading} 
          onRefresh={handleRefresh}
        />
      </Grid>

      {/* Pagination Below the Table - API-based pagination */}
      {showPagination && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginTop={2}
        >
          {/* Left Arrow */}
          <IconButton
            size="small"
            onClick={() => {
              setPage((prev) => Math.max(prev - 1, 1));
              setLoading(true);
              setSummaryLoading(true);
            }}
            disabled={page === 1 || loading}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>

          {/* Page Info in Center */}
          <Typography variant="body2" style={{ margin: "0 8px" }}>
            {`${page} out of ${totalPages} pages`}
          </Typography>

          {/* Right Arrow */}
          <IconButton
            size="small"
            onClick={() => {
              setPage((prev) => Math.min(prev + 1, totalPages));
              setLoading(true);
              setSummaryLoading(true);
            }}
            disabled={page >= totalPages || loading}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      )}
    </div>
  );
};

const mapStatetoProps = (state) => ({});

export default connect(mapStatetoProps, {})(withStyles(styles)(withRouter(Integration)));